import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import api from "@/utils/api";
import useCustomerAmounts, { amountText } from "@/hooks/useCustomerAmounts";
import { awaitingSendtoo, PENDING_TEXT } from "@/utils/portalBooking";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Package, Clock } from "lucide-react";
import StatusBadge from "@/components/shared/StatusBadge";

export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const amt = useCustomerAmounts(shipment?.id ? [shipment.id] : [])[shipment?.id];
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/shipments/${id}`);
        setShipment(r.data);
      } catch (e) {
        toast.error("Failed to load shipment details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-10 text-center text-slate-400">Loading...</div>
      </DashboardLayout>
    );
  }

  if (!shipment) {
    return (
      <DashboardLayout>
        <div className="py-10 text-center text-slate-400">Shipment not found</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <button
          onClick={() => navigate("/customer/shipments")}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Shipments
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Order {shipment.order_id || shipment.tracking_id}
            </h1>
            <p className="text-slate-500 text-sm">Tracking ID: {shipment.tracking_id}</p>
          </div>
          <StatusBadge status={shipment.status} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase mb-2">
              <MapPin className="w-4 h-4" /> Pickup Location
            </div>
            <div className="font-semibold text-slate-800">{shipment.origin?.name}</div>
            <div className="text-sm text-slate-600 mt-1">{shipment.origin?.address}</div>
            <div className="text-sm text-slate-600">
              {shipment.origin?.city}, {shipment.origin?.state} — {shipment.origin?.pincode}
            </div>
            <div className="text-sm text-slate-500 mt-1">{shipment.origin?.phone}</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase mb-2">
              <MapPin className="w-4 h-4" /> Drop Location
            </div>
            <div className="font-semibold text-slate-800">{shipment.destination?.name}</div>
            <div className="text-sm text-slate-600 mt-1">{shipment.destination?.address}</div>
            <div className="text-sm text-slate-600">
              {shipment.destination?.city}, {shipment.destination?.state} — {shipment.destination?.pincode}
            </div>
            <div className="text-sm text-slate-500 mt-1">{shipment.destination?.phone}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase mb-3">
            <Package className="w-4 h-4" /> Package Details
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-slate-400 text-xs">Weight (Declared)</div>
              <div className="font-semibold">{shipment.package?.weight || shipment.chargeable_weight} kg</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">Number of Boxes</div>
              <div className="font-semibold">{shipment.box_count || 1}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">LR Number</div>
              <div className="font-semibold">{awaitingSendtoo(shipment) ? <span className="text-xs text-amber-700">{PENDING_TEXT}</span> : (shipment.lr_number || "—")}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">{amt?.amount != null ? amt.label : "Amount"}</div>
              <div className="font-semibold" data-testid="customer-amount">{amountText(amt)}</div>
              {amt?.invoice_number && <div className="text-[11px] text-slate-400">Invoice {amt.invoice_number}</div>}
            </div>
          </div>
          <div className="mt-4">
            <div className="text-slate-400 text-xs mb-2">
              {shipment.box_count > 1 ? "AWB Numbers (per box)" : "AWB"}
            </div>
            {awaitingSendtoo(shipment) ? (
              <span className="text-xs text-amber-700" data-testid="awb-pending">{PENDING_TEXT} - the AWB / LR appears once your shipment is confirmed with the carrier.</span>
            ) : shipment.is_multibox && Array.isArray(shipment.awb_numbers) && shipment.awb_numbers.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {shipment.awb_numbers.map((a, i) => (
                  <span key={i} className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                    Box {i + 1}: {a}
                  </span>
                ))}
              </div>
            ) : (
              <span className="font-mono text-sm font-semibold">{shipment.awb || shipment.awb_number || "—"}</span>
            )}
          </div>
        </div>

        {shipment.v2_actual_weight ? (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="weight-verified">
            <div className="text-xs font-bold text-slate-500 uppercase mb-1">Weight verified by carrier</div>
            <div className="text-sm font-semibold text-slate-800">{shipment.v2_actual_weight} kg</div>
            <p className="text-xs text-slate-500 mt-1">The final amount appears here once your invoice is raised.</p>
          </div>
        ) : null}

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase mb-3">
            <Clock className="w-4 h-4" /> Shipment Timeline
          </div>
          {(shipment.status_history || []).length === 0 ? (
            <div className="text-sm text-slate-400">No timeline events yet</div>
          ) : (
            <div className="space-y-3">
              {(shipment.status_history || []).map((h, i) => (
                <div key={i} className="flex gap-3 border-l-2 border-brand-200 pl-3">
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-800">{h.status}</div>
                    {h.note && <div className="text-xs text-slate-500">{h.note}</div>}
                    <div className="text-xs text-slate-400">
                      {new Date(h.timestamp).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Link
          to="/customer/pickup-requests"
          className="inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:text-brand-700 hover:bg-brand-50 px-3 py-2 rounded transition"
        >
          📦 Schedule Pickup for this Shipment
        </Link>
      </div>
    </DashboardLayout>
  );
}
